/**
 * F11 Task 19 — typed wrappers for the admin B2B-account endpoints.
 *
 * Endpoints covered:
 *   GET    /admin/b2b-accounts                       → paginated, searchable list
 *   GET    /admin/b2b-accounts/:id                   → detail + owner + venues + claims
 *   PATCH  /admin/b2b-accounts/:id                   → edit legal/display/tax/country
 *   POST   /admin/b2b-accounts/:id/suspend           → cascade suspend (reason required)
 *   POST   /admin/b2b-accounts/:id/resume            → flip status back to active
 *   PATCH  /admin/venues/:venueId/owner              → force-reassign venue ownership
 */
import type {
  AdminCreateActorInput,
  AdminPageMeta,
  B2bAccount,
  B2bAccountStatus,
  NameLockOverride,
  ReassignVenueOwnershipInput,
  UpdateB2bAccountInput,
  Venue,
  VenueClaim,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

/**
 * `PATCH /admin/b2b-accounts/:id` body: the frozen update input plus the F09 v2
 * name-lock override (§11 point 5). A display/legal-name change on a verified
 * club/organizer account returns 422 `NAME_LOCKED_VERIFIED` unless
 * `nameLockOverride` is sent; the override needs `verifications:review` on top
 * of `b2b_accounts:write`.
 */
export type AdminUpdateB2bAccountRequest = UpdateB2bAccountInput & {
  readonly nameLockOverride?: NameLockOverride;
};

// ─── Response envelopes ────────────────────────────────────────────────────────

export interface B2bAccountListItem extends B2bAccount {
  readonly venueCount: number;
  readonly activeClaimCount: number;
}

export interface B2bAccountListResponse {
  readonly items: readonly B2bAccountListItem[];
  readonly nextCursor: string | null;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface B2bAccountOwner {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly roles: readonly string[];
  readonly status: string;
  readonly createdAt: string;
}

export interface B2bAccountDetailResponse {
  readonly account: B2bAccount;
  readonly primaryOwner: B2bAccountOwner | null;
  readonly venues: readonly Venue[];
  readonly claims: readonly VenueClaim[];
}

// ─── List options ──────────────────────────────────────────────────────────────

export interface B2bAccountListOpts {
  readonly status?: B2bAccountStatus | 'all';
  readonly q?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

/**
 * Admin Super-Powers — POST /admin/b2b-accounts (super_admin, `b2b_accounts:create`)
 * result. Provisions a B2B actor (venue/trainer/club/organizer) on a user's
 * behalf; the server appends the kind-correct role + companion profile.
 * `trainerProfileCreated` is true for `accountKind='trainer'`; `organizerApproved`
 * is true for `accountKind='organizer'` (auto-approved at creation).
 */
export interface AdminCreateActorResult {
  readonly account: B2bAccount;
  readonly trainerProfileCreated: boolean;
  readonly organizerApproved: boolean;
}

export interface SuspendInput {
  readonly reason: string;
}

export interface ResumeInput {
  readonly note?: string;
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminB2bAccountApi = {
  /** GET /admin/b2b-accounts — paginated list with optional status/q filters. */
  listAccounts: (opts?: B2bAccountListOpts): Promise<B2bAccountListResponse> =>
    adminFetch<B2bAccountListResponse>(
      `/admin/b2b-accounts${buildQuery({
        status: opts?.status,
        q: opts?.q,
        cursor: opts?.cursor,
        limit: opts?.limit,
        page: opts?.page,
        perPage: opts?.perPage,
      })}`,
    ),

  /**
   * POST /admin/b2b-accounts — provision a B2B actor on a user's behalf.
   * 404 USER_NOT_FOUND (no such owner), 409 ACCOUNT_ALREADY_EXISTS (MVP 1:1 —
   * the user already owns an account; `existingAccount` rides on the error body),
   * 422 VALIDATION_ERROR.
   */
  createAccount: async (input: AdminCreateActorInput): Promise<AdminCreateActorResult> => {
    const res = await adminFetch<{ data: AdminCreateActorResult }>('/admin/b2b-accounts', {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return res.data;
  },

  /** GET /admin/b2b-accounts/:id — full detail. */
  getAccountDetail: (id: string): Promise<B2bAccountDetailResponse> =>
    adminFetch<B2bAccountDetailResponse>(`/admin/b2b-accounts/${id}`),

  /** PATCH /admin/b2b-accounts/:id — update editable fields (+ optional name-lock override). */
  updateAccount: (
    id: string,
    body: AdminUpdateB2bAccountRequest,
  ): Promise<{ account: B2bAccount }> =>
    adminFetch<{ account: B2bAccount }>(`/admin/b2b-accounts/${id}`, {
      method: 'PATCH',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/b2b-accounts/:id/suspend — required reason ≥10 chars. */
  suspendAccount: (id: string, body: SuspendInput): Promise<{ account: B2bAccount }> =>
    adminFetch<{ account: B2bAccount }>(`/admin/b2b-accounts/${id}/suspend`, {
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    }),

  /** POST /admin/b2b-accounts/:id/resume — empty or `{ note? }`. */
  resumeAccount: (id: string, body: ResumeInput = {}): Promise<{ account: B2bAccount }> =>
    adminFetch<{ account: B2bAccount }>(`/admin/b2b-accounts/${id}/resume`, {
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    }),

  /** PATCH /admin/venues/:venueId/owner — force-reassign venue ownership. */
  reassignVenueOwner: (
    venueId: string,
    body: ReassignVenueOwnershipInput,
  ): Promise<{ data: Venue }> =>
    adminFetch<{ data: Venue }>(`/admin/venues/${venueId}/owner`, {
      method: 'PATCH',
      body: body as Record<string, unknown>,
    }),
};
